<script>
  import { router, page } from '@inertiajs/svelte'
  import dayjs from 'dayjs'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import FilterTabs from '../../components/filter_tabs.svelte'
  import SearchInput from '../../components/search_input.svelte'
  import FilterSelect from '../../components/filter_select.svelte'
  import FilterDate from '../../components/filter_date.svelte'
  import EntityList from '../../components/entity/entity_list.svelte'
  import DisplayOptions from '../../components/entity/display_options.svelte'
  import Paginator from '../../components/paginator.svelte'
  import { Button } from '../../components/ui'
  import { confirm } from '../../components/confirm.js'

  let {
    moments = [],
    pagy,
    total_count = null,
    mode = 'all',
    filters = {},
    media_type_options = [],
    visibility_options = [],
    sort_options = [],
    default_sort = 'newest'
  } = $props()

  let viewMode = $state(null)

  const tabs = [
    { key: 'all', label: 'All Moments' },
    { key: 'flagged', label: 'Flagged' },
    { key: 'hidden', label: 'Hidden' }
  ]

  const EMPTY_MESSAGES = {
    all: 'No moments found.',
    flagged: 'No flagged moments to review.',
    hidden: 'No hidden moments.'
  }

  const pushFilter = (key, value) => {
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set(key, value)
    } else {
      url.searchParams.delete(key)
    }
    url.searchParams.delete('page')
    router.get(`${url.pathname}${url.search}`, {}, { preserveState: true, preserveScroll: true })
  }

  // The tab's default sort stays implicit (out of the URL) so switching tabs flips
  // between oldest-report-first on Flagged and newest-first elsewhere; any other sort
  // is an explicit choice and sticks across tabs.
  const pushSort = (value) => pushFilter('sort', value === default_sort ? null : value)

  const shiftDate = (days) => {
    if (!filters.date) return
    pushFilter('date', dayjs(filters.date).add(days, 'day').format('YYYY-MM-DD'))
  }

  const approveMoment = (moment) => {
    router.patch(`/moments/${moment.id}/review`, { decision: 'approve' }, { preserveScroll: true, preserveState: true })
  }

  const deleteMoment = async (moment) => {
    const ok = await confirm({
      title: 'Delete Moment',
      variant: 'error',
      body: moment.would_retract_points
        ? 'This will delete the moment and retract any points awarded for it. Are you sure?'
        : 'This will delete the moment. No points are retracted. Are you sure?',
      confirmLabel: 'Delete'
    })
    if (!ok) return
    router.patch(`/moments/${moment.id}/review`, { decision: 'delete' }, { preserveScroll: true, preserveState: true })
  }
</script>

<Layout>
  <PageHeader title="Moments" subtitle="User photo and video posts, with moderation" count={pagy.count} total={total_count} />

  <FilterBar>
    {#snippet start()}
      <FilterTabs {tabs} active={mode} param="mode" />
    {/snippet}
    {#snippet end()}
      <DisplayOptions entity="moment" bind:viewMode />
    {/snippet}
  </FilterBar>

  <FilterBar>
    {#snippet start()}
      <SearchInput value={filters.search || ''} param="search" placeholder="Search users…" />
      <FilterSelect
        label="Place"
        value={filters.place_id || ''}
        placeholder="Any place"
        source="places"
        sourceParams={{ area_id: $page.props.current_area_id }}
        onchange={(v) => pushFilter('place_id', v)}
      />
      <FilterSelect
        label="Media"
        width="w-32"
        value={filters.media_type || ''}
        placeholder="Any"
        options={media_type_options}
        onchange={(v) => pushFilter('media_type', v)}
      />
      <FilterSelect
        label="Visibility"
        width="w-32"
        value={filters.visibility || ''}
        placeholder="Any"
        options={visibility_options}
        onchange={(v) => pushFilter('visibility', v)}
      />
      <FilterDate id="moment-date" nav value={filters.date || ''} onchange={(v) => pushFilter('date', v)} onshift={shiftDate} />
    {/snippet}
    {#snippet end()}
      <FilterSelect
        label="Sort"
        width="w-40"
        value={filters.sort || default_sort}
        placeholder={null}
        options={sort_options}
        onchange={pushSort}
      />
    {/snippet}
  </FilterBar>

  <EntityList entity="moment" rows={moments} {viewMode} emptyMessage={EMPTY_MESSAGES[mode]}>
    {#snippet actions(moment)}
      <Button
        variant="ghost"
        size="xs"
        square
        icon="check"
        disabled={!moment.hidden}
        title={moment.hidden ? 'Approve' : 'Already visible'}
        aria-label="Approve"
        onclick={() => approveMoment(moment)}
      />
      <Button variant="ghost" size="xs" square icon="delete" title="Delete" aria-label="Delete" class="text-error" onclick={() => deleteMoment(moment)} />
    {/snippet}
  </EntityList>

  {#if pagy}<Paginator {...pagy} />{/if}
</Layout>
